import React, { useState } from 'react';
import { View, Text, TextInput } from 'react-native';
import i18n from 'i18n-js';

import ColorWrapper from '@styles/colors';
import { font } from '@styles/fonts';
import { scale } from '@components/frame';
import TouchableElement from '@components/common/touchableElement';
import { MoveToPositionModalProps } from './types';

const MoveToPositionModal: React.FC<MoveToPositionModalProps> = ({
  hide,
  maxPosition,
  currentPosition,
  onSubmit,
  C,
}) => {
  const [value, setValue] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = () => {
    const position = parseInt(value, 10);

    if (isNaN(position)) {
      setError(i18n.t('Please enter a valid number'));
      return;
    }

    if (position < 1 || position > maxPosition) {
      setError(i18n.t('Position must be between 1 and {{max}}', { max: maxPosition }));
      return;
    }

    if (position === currentPosition) {
      hide();
      return;
    }

    onSubmit(position);
    hide();
  };

  return (
    <View style={{ padding: scale(24), minWidth: scale(300) }}>
      <Text
        style={[
          font['Headline Large'],
          {
            color: C('Label/Light/Primary'),
            marginBottom: scale(16),
          },
        ]}
      >
        {i18n.t('Move To Position')}
      </Text>

      <Text
        style={[
          font['Body'],
          {
            color: C('Label/Light/Secondary'),
            marginBottom: scale(16),
          },
        ]}
      >
        {i18n.t('Select a position between {{min}} and {{max}}', { min: 1, max: maxPosition })}
      </Text>

      <Text
        style={[
          font['Caption 1'],
          {
            color: C('Label/Light/Tertiary'),
            marginBottom: scale(8),
          },
        ]}
      >
        {i18n.t('Current position: {{position}}', { position: currentPosition })}
      </Text>

      <TextInput
        style={[
          font['Body'],
          {
            borderWidth: 1,
            borderColor: error ? C('Chart/Light/Warning') : C('Hairline/Light/No trans [mobile]'),
            borderRadius: scale(8),
            padding: scale(12),
            color: C('Label/Light/Primary'),
            backgroundColor: C('Background/Light/Field lighter'),
            marginBottom: scale(8),
          },
        ]}
        value={value}
        onChangeText={(text) => {
          setValue(text);
          setError('');
        }}
        keyboardType="number-pad"
        placeholder={i18n.t('Enter position')}
        placeholderTextColor={C('Label/Light/Tertiary')}
        autoFocus
        onSubmitEditing={handleSubmit}
      />

      {error ? (
        <Text
          style={[
            font['Caption 1'],
            {
              color: C('Chart/Light/Warning'),
              marginBottom: scale(8),
            },
          ]}
        >
          {error}
        </Text>
      ) : null}

      <View
        style={{
          flexDirection: 'row',
          justifyContent: 'flex-end',
          gap: scale(12),
          marginTop: scale(16),
        }}
      >
        <TouchableElement onPress={hide}>
          {({ pressHoverColor, boxStyle }) => (
            <View
              style={[
                boxStyle,
                {
                  paddingVertical: scale(10),
                  paddingHorizontal: scale(20),
                  borderRadius: scale(8),
                  backgroundColor: C(pressHoverColor('Gray/Light/5')),
                },
              ]}
            >
              <Text style={[font['Button 1'], { color: C('Label/Light/Primary') }]}>{i18n.t('Cancel')}</Text>
            </View>
          )}
        </TouchableElement>

        <TouchableElement onPress={handleSubmit}>
          {({ pressHoverColor, boxStyle }) => (
            <View
              style={[
                boxStyle,
                {
                  paddingVertical: scale(10),
                  paddingHorizontal: scale(20),
                  borderRadius: scale(8),
                  backgroundColor: C(pressHoverColor('System/Light/Blue')),
                },
              ]}
            >
              <Text style={[font['Button 1'], { color: C('Label/Light/AlwaysLight Primary') }]}>{i18n.t('Move')}</Text>
            </View>
          )}
        </TouchableElement>
      </View>
    </View>
  );
};

export default ColorWrapper(MoveToPositionModal);
